import React, { useState, useEffect, useMemo } from 'react';
import {
    Loader2,
    Save,
    XCircle,
    AlertCircle,
    Search,
    UserCheck,
    Users,
    User,
    Radio,
    ArrowRightCircle,
    ArrowLeftCircle,
    Clock,
    Check,
    X,
    Building2
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import CustomDateTimePicker from './common/CustomDateTimePicker';

const RecordType = {
    Entry: 0,
    Exit: 1
};

const QUICK_NOTE_TEMPLATES = [
    'Kart Unutuldu',
    'Okuyucu / Turnike Algılamadı',
    'Dış Görev / Saha Giriş-Çıkışı',
    'İnsan Kaynakları Manuel Düzeltme',
    'Yeni Personel (Kart Tanımlama Öncesi)'
];

export default function PassRecordForm({
    record,
    onSave,
    onCancel,
    isNew = true,
    submitting: propSubmitting = false,
    formError: propFormError = null
}) {
    const initialRecordTime = record?.recordTime ? new Date(record.recordTime) : new Date();

    const getInitialRecordType = () => {
        if (record?.recordType !== undefined && record?.recordType !== null) {
            if (typeof record.recordType === 'string') {
                return record.recordType === 'Entry' ? RecordType.Entry : RecordType.Exit;
            }
            return Number(record.recordType);
        }
        return RecordType.Entry;
    };

    // 'single' | 'bulk' (bulk only available when isNew is true)
    const [entryMode, setEntryMode] = useState('single');

    const [formData, setFormData] = useState({
        id: record?.id || null,
        employeeId: record?.employeeId || '',
        recordTime: initialRecordTime,
        recordType: getInitialRecordType(),
        deviceSerialNumber: record?.deviceSerialNumber || '',
        deviceId: record?.deviceId || null,
        notes: record?.notes || ''
    });

    // Selected employee object in single mode
    const [selectedEmployee, setSelectedEmployee] = useState(
        record?.employeeId
            ? {
                  id: record.employeeId,
                  fullName: record.employeeFullName || `Personel #${record.employeeId}`,
                  registrationNumber: record.employeeRegistrationNumber || '',
                  departmentName: record.departmentName || ''
              }
            : null
    );

    // Selected employees map in bulk mode: [{ id, fullName, registrationNumber, departmentName }]
    const [selectedBulkEmployees, setSelectedBulkEmployees] = useState([]);

    // Employee search & filter states
    const [empSearchTerm, setEmpSearchTerm] = useState('');
    const [empDepartmentFilter, setEmpDepartmentFilter] = useState('');
    const [employeeResults, setEmployeeResults] = useState([]);
    const [totalMatchingEmployees, setTotalMatchingEmployees] = useState(0);
    const [searchingEmployees, setSearchingEmployees] = useState(false);

    // Lookups (accessPoints & departments)
    const [accessPoints, setAccessPoints] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loadingLookups, setLoadingLookups] = useState(true);

    const [localError, setLocalError] = useState(null);
    const effectiveError = localError || propFormError;
    const submitting = propSubmitting;

    // Load form lookups (accessPoints + departments)
    useEffect(() => {
        let isMounted = true;
        setLoadingLookups(true);
        apiClient('passrecord/form-lookups')
            .then((res) => {
                if (!isMounted) return;
                const normalizedAccessPoints = (res?.accessPoints || []).map((ap) => {
                    const dir = ap.direction || '';
                    const isEntry = ap.isEntry !== undefined ? ap.isEntry : (dir === 'Entry' || dir === 'Both');
                    const isExit = ap.isExit !== undefined ? ap.isExit : (dir === 'Exit' || dir === 'Both');
                    return {
                        ...ap,
                        value: ap.value ?? ap.serialValue ?? ap.rawDeviceSerial ?? ap.name,
                        label: ap.label ?? ap.displayLabel ?? ap.name,
                        isEntry,
                        isExit
                    };
                });
                setAccessPoints(normalizedAccessPoints);
                setDepartments(res?.departments || []);
            })
            .catch(() => {
                if (!isMounted) return;
                setLocalError('Okuyucu ve departman listesi yüklenemedi.');
            })
            .finally(() => {
                if (isMounted) setLoadingLookups(false);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const shouldSearchEmployees = empSearchTerm.trim().length >= 2 || Boolean(empDepartmentFilter);

    // Debounced server-side employee search (only runs when >= 2 chars typed or department selected; returns max 30 rows)
    useEffect(() => {
        if (!shouldSearchEmployees) {
            setEmployeeResults([]);
            setTotalMatchingEmployees(0);
            setSearchingEmployees(false);
            return;
        }

        let isMounted = true;
        const timeoutId = setTimeout(async () => {
            try {
                setSearchingEmployees(true);
                const params = new URLSearchParams({ limit: '30' });
                if (empSearchTerm.trim()) params.set('q', empSearchTerm.trim());
                if (empDepartmentFilter) params.set('departmentId', empDepartmentFilter);

                const res = await apiClient(`passrecord/employee-search?${params.toString()}`);
                if (!isMounted) return;
                const rawItems = Array.isArray(res) ? res : (res?.items || []);
                const items = rawItems.map((emp) => ({
                    ...emp,
                    id: emp.id,
                    fullName: emp.fullName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
                    registrationNumber: emp.registrationNumber ?? emp.sicilNo ?? '',
                    departmentName: emp.departmentName ?? ''
                }));
                setEmployeeResults(items);
                setTotalMatchingEmployees(res?.totalMatching ?? items.length);
            } catch {
                if (isMounted) {
                    setEmployeeResults([]);
                    setTotalMatchingEmployees(0);
                }
            } finally {
                if (isMounted) setSearchingEmployees(false);
            }
        }, 250);

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
        };
    }, [shouldSearchEmployees, empSearchTerm, empDepartmentFilter]);

    const selectedAccessPoint = useMemo(() => {
        if (!formData.deviceSerialNumber) return null;
        return accessPoints.find(
            (ap) =>
                ap.value === formData.deviceSerialNumber ||
                ap.serialNumber === formData.deviceSerialNumber ||
                ap.label === formData.deviceSerialNumber
        );
    }, [formData.deviceSerialNumber, accessPoints]);

    // Auto-select direction when an access point / slot has a specific direction
    useEffect(() => {
        if (selectedAccessPoint) {
            if (selectedAccessPoint.isEntry && !selectedAccessPoint.isExit) {
                setFormData((prev) => ({
                    ...prev,
                    recordType: RecordType.Entry,
                    deviceId: selectedAccessPoint.deviceId ?? prev.deviceId
                }));
            } else if (!selectedAccessPoint.isEntry && selectedAccessPoint.isExit) {
                setFormData((prev) => ({
                    ...prev,
                    recordType: RecordType.Exit,
                    deviceId: selectedAccessPoint.deviceId ?? prev.deviceId
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    deviceId: selectedAccessPoint.deviceId ?? prev.deviceId
                }));
            }
        }
    }, [selectedAccessPoint]);

    const handleSelectSingleEmployee = (emp) => {
        setSelectedEmployee(emp);
        setFormData((prev) => ({ ...prev, employeeId: emp.id }));
        setLocalError(null);
    };

    const toggleBulkEmployee = (emp) => {
        setSelectedBulkEmployees((prev) => {
            const exists = prev.some((x) => x.id === emp.id);
            if (exists) return prev.filter((x) => x.id !== emp.id);
            return [...prev, emp];
        });
        setLocalError(null);
    };

    const selectAllVisibleEmployees = () => {
        setSelectedBulkEmployees((prev) => {
            const existingIds = new Set(prev.map((x) => x.id));
            const merged = [...prev];
            employeeResults.forEach((emp) => {
                if (!existingIds.has(emp.id)) {
                    merged.push(emp);
                    existingIds.add(emp.id);
                }
            });
            return merged;
        });
        setLocalError(null);
    };

    const applyQuickTime = (hours, minutes) => {
        const base = formData.recordTime ? new Date(formData.recordTime) : new Date();
        base.setHours(hours, minutes, 0, 0);
        setFormData((prev) => ({ ...prev, recordTime: base }));
        setLocalError(null);
    };

    const applyNowTime = () => {
        setFormData((prev) => ({ ...prev, recordTime: new Date() }));
        setLocalError(null);
    };

    const appendQuickNote = (template) => {
        setFormData((prev) => {
            const current = (prev.notes || '').trim();
            if (!current) return { ...prev, notes: template };
            if (current.includes(template)) return prev;
            return { ...prev, notes: `${current} - ${template}` };
        });
    };

    const isEntryDisabled =
        submitting || (selectedAccessPoint && !selectedAccessPoint.isEntry && selectedAccessPoint.isExit);
    const isExitDisabled =
        submitting || (selectedAccessPoint && selectedAccessPoint.isEntry && !selectedAccessPoint.isExit);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError(null);

        if (!formData.recordTime) {
            setLocalError('Lütfen geçerli bir Kayıt Zamanı seçiniz.');
            return;
        }

        const nowPlus1Min = new Date(Date.now() + 60 * 1000);
        if (new Date(formData.recordTime) > nowPlus1Min) {
            setLocalError('İleri tarihli / saatli geçiş kaydı eklenemez.');
            return;
        }

        if (isNew && entryMode === 'bulk') {
            if (selectedBulkEmployees.length === 0) {
                setLocalError('Lütfen en az bir personel seçiniz.');
                return;
            }
            try {
                await onSave({
                    isBulk: true,
                    employeeIds: selectedBulkEmployees.map((x) => x.id),
                    recordTime: formData.recordTime,
                    recordType: formData.recordType,
                    deviceSerialNumber: formData.deviceSerialNumber || null,
                    deviceId: formData.deviceId || null,
                    notes: formData.notes
                });
            } catch (err) {
                setLocalError(err.message || 'Toplu kayıt oluşturulurken bir hata oluştu.');
            }
            return;
        }

        if (!formData.employeeId) {
            setLocalError('Lütfen bir personel seçiniz.');
            return;
        }

        try {
            await onSave({
                ...formData,
                employeeId: Number(formData.employeeId),
                deviceSerialNumber: formData.deviceSerialNumber || null,
                deviceId: formData.deviceId || null
            });
        } catch (err) {
            setLocalError(err.message || 'Kaydetme işlemi sırasında bir hata oluştu.');
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-5">
            {effectiveError && (
                <div className="bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 p-3.5 rounded-xl text-sm flex items-start gap-2.5">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-red-500" />
                    <span>{effectiveError}</span>
                </div>
            )}

            {/* Yeni Kayıtta Tekli / Çoklu Personel Mod Seçimi */}
            {isNew && (
                <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
                    <button
                        type="button"
                        onClick={() => {
                            setEntryMode('single');
                            setLocalError(null);
                        }}
                        className={`py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                            entryMode === 'single'
                                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                    >
                        <User size={16} />
                        Tekli Personel Kaydı
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setEntryMode('bulk');
                            setLocalError(null);
                        }}
                        className={`py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                            entryMode === 'bulk'
                                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                    >
                        <Users size={16} />
                        Çoklu Personel (Toplu Ekle)
                        {selectedBulkEmployees.length > 0 && (
                            <span className="px-1.5 py-0.5 text-[11px] rounded-full bg-blue-600 text-white">
                                {selectedBulkEmployees.length}
                            </span>
                        )}
                    </button>
                </div>
            )}

            {/* Personel Seçim Alanı (30.000 Personel Uyumlu Sunucu Tabanlı Arama) */}
            <div className="bg-gray-50/70 dark:bg-gray-900/40 p-4 rounded-xl border border-gray-200 dark:border-gray-700 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-1.5">
                        {entryMode === 'bulk' ? <Users size={16} className="text-blue-500" /> : <UserCheck size={16} className="text-blue-500" />}
                        {entryMode === 'bulk' ? 'Personelleri Seçin *' : 'Personel Seçimi *'}
                    </label>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        Ad, soyad veya sicil numarası ile anlık arama
                    </span>
                </div>

                {/* Tekli Modda Seçili Personel Kartı */}
                {entryMode === 'single' && selectedEmployee && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800">
                        <div className="flex flex-col">
                            <span className="text-sm font-bold text-blue-900 dark:text-blue-100">
                                {selectedEmployee.fullName}
                            </span>
                            <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                                {selectedEmployee.registrationNumber && (
                                    <span className="font-mono">Sicil: #{selectedEmployee.registrationNumber}</span>
                                )}
                                {selectedEmployee.departmentName && (
                                    <span>• {selectedEmployee.departmentName}</span>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setSelectedEmployee(null);
                                setFormData((prev) => ({ ...prev, employeeId: '' }));
                            }}
                            className="px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/50 rounded-md transition-colors"
                        >
                            Değiştir
                        </button>
                    </div>
                )}

                {/* Çoklu Modda Seçilen Personel Chipleri */}
                {entryMode === 'bulk' && selectedBulkEmployees.length > 0 && (
                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                            <span className="font-medium text-blue-600 dark:text-blue-400">
                                Seçilen Personeller ({selectedBulkEmployees.length} kişi)
                            </span>
                            <button
                                type="button"
                                onClick={() => setSelectedBulkEmployees([])}
                                className="text-rose-600 dark:text-rose-400 hover:underline text-[11px]"
                            >
                                Seçimi Temizle
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                            {selectedBulkEmployees.map((emp) => (
                                <span
                                    key={emp.id}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200"
                                >
                                    {emp.fullName}
                                    <button
                                        type="button"
                                        onClick={() => toggleBulkEmployee(emp)}
                                        className="hover:text-rose-600"
                                    >
                                        <X size={12} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Arama ve Departman Filtresi (Tekli modda personel seçilmemişse veya Çoklu moddaysa göster) */}
                {(entryMode === 'bulk' || !selectedEmployee) && (
                    <div className="space-y-2">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <div className="sm:col-span-2 relative">
                                <input
                                    type="text"
                                    value={empSearchTerm}
                                    onChange={(e) => setEmpSearchTerm(e.target.value)}
                                    placeholder="Personel adı veya sicil no yazın..."
                                    className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                {empSearchTerm && (
                                    <button
                                        type="button"
                                        onClick={() => setEmpSearchTerm('')}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                            <div className="relative">
                                <select
                                    value={empDepartmentFilter}
                                    onChange={(e) => setEmpDepartmentFilter(e.target.value)}
                                    className="w-full pl-2.5 pr-6 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Tüm Departmanlar</option>
                                    {departments.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {!shouldSearchEmployees ? (
                            <div className="py-3 px-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white/60 dark:bg-gray-800/50 text-center text-xs text-gray-500 dark:text-gray-400">
                                Personel bulmak için arama kutusuna <strong>en az 2 karakter</strong> (Ad, Soyad veya Sicil No) yazın ya da <strong>Departman</strong> seçin.
                            </div>
                        ) : (
                            <>
                                {!searchingEmployees && employeeResults.length > 0 && (
                                    <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                            Eşleşen <strong>{totalMatchingEmployees.toLocaleString('tr-TR')}</strong> personelden ilk <strong>{employeeResults.length}</strong> kayıt listeleniyor
                                        </span>
                                        {entryMode === 'bulk' && (
                                            <button
                                                type="button"
                                                onClick={selectAllVisibleEmployees}
                                                className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                                            >
                                                <Check size={13} /> Listelenen {employeeResults.length} Personeli Ekle
                                            </button>
                                        )}
                                    </div>
                                )}

                                <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700/60">
                                    {searchingEmployees ? (
                                        <div className="py-6 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                                            <Loader2 size={16} className="animate-spin text-blue-500" />
                                            Personeller aranıyor...
                                        </div>
                                    ) : employeeResults.length > 0 ? (
                                        employeeResults.map((emp) => {
                                            const isSelectedBulk =
                                                entryMode === 'bulk' && selectedBulkEmployees.some((x) => x.id === emp.id);
                                            return (
                                                <button
                                                    key={emp.id}
                                                    type="button"
                                                    onClick={() =>
                                                        entryMode === 'bulk'
                                                            ? toggleBulkEmployee(emp)
                                                            : handleSelectSingleEmployee(emp)
                                                    }
                                                    className={`w-full px-3 py-2 text-left flex items-center justify-between text-xs sm:text-sm transition-colors ${
                                                        isSelectedBulk
                                                            ? 'bg-blue-50/90 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                                                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/60 text-gray-800 dark:text-gray-100'
                                                    }`}
                                                >
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">{emp.fullName}</span>
                                                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                                            {emp.registrationNumber ? `#${emp.registrationNumber}` : 'Sicil Yok'}
                                                            {emp.departmentName ? ` • ${emp.departmentName}` : ''}
                                                        </span>
                                                    </div>
                                                    {entryMode === 'bulk' ? (
                                                        <span
                                                            className={`w-5 h-5 rounded flex items-center justify-center border ${
                                                                isSelectedBulk
                                                                    ? 'bg-blue-600 border-blue-600 text-white'
                                                                    : 'border-gray-300 dark:border-gray-600'
                                                            }`}
                                                        >
                                                            {isSelectedBulk && <Check size={13} />}
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                                            Seç
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })
                                    ) : (
                                        <div className="py-5 text-center text-xs text-gray-500 dark:text-gray-400">
                                            Arama kriterine uygun personel bulunamadı.
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Geçiş Noktası / Okuyucu Slotu ve Geçiş Yönü */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label
                        htmlFor="deviceSerialNumber"
                        className="block text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1.5 flex items-center gap-1.5"
                    >
                        <Radio size={15} className="text-indigo-500" />
                        Geçiş Noktası / Okuyucu Slotu
                    </label>
                    <select
                        id="deviceSerialNumber"
                        name="deviceSerialNumber"
                        value={formData.deviceSerialNumber}
                        onChange={(e) => {
                            const val = e.target.value;
                            const matched = accessPoints.find((ap) => ap.value === val);
                            setFormData((prev) => ({
                                ...prev,
                                deviceSerialNumber: val,
                                deviceId: matched?.deviceId ?? null
                            }));
                            setLocalError(null);
                        }}
                        disabled={submitting || loadingLookups}
                        className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Manuel Kayıt (Cihaz / Okuyucu Belirtilmedi)</option>
                        {accessPoints.map((ap) => (
                            <option key={ap.key} value={ap.value}>
                                {ap.label}
                            </option>
                        ))}
                    </select>
                    {selectedAccessPoint && (
                        <p className="mt-1 text-[11px] text-indigo-600 dark:text-indigo-400">
                            {selectedAccessPoint.isEntry && !selectedAccessPoint.isExit
                                ? 'Seçilen okuyucu Giriş yönüne ayarlıdır.'
                                : !selectedAccessPoint.isEntry && selectedAccessPoint.isExit
                                ? 'Seçilen okuyucu Çıkış yönüne ayarlıdır.'
                                : 'Seçilen geçiş noktası çift yönlüdür.'}
                        </p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1.5">
                        Geçiş Yönü (Tipi) *
                    </label>
                    <div className="grid grid-cols-2 gap-2.5">
                        <button
                            type="button"
                            disabled={isEntryDisabled}
                            onClick={() => setFormData((prev) => ({ ...prev, recordType: RecordType.Entry }))}
                            className={`py-2.5 px-3 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                                formData.recordType === RecordType.Entry
                                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-emerald-400'
                            } ${isEntryDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                            <ArrowRightCircle size={17} />
                            Giriş
                        </button>
                        <button
                            type="button"
                            disabled={isExitDisabled}
                            onClick={() => setFormData((prev) => ({ ...prev, recordType: RecordType.Exit }))}
                            className={`py-2.5 px-3 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                                formData.recordType === RecordType.Exit
                                    ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-rose-400'
                            } ${isExitDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                            <ArrowLeftCircle size={17} />
                            Çıkış
                        </button>
                    </div>
                </div>
            </div>

            {/* Kayıt Zamanı & Hızlı Saat Seçiciler */}
            <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-1.5">
                        <Clock size={15} className="text-blue-500" />
                        Kayıt Tarihi ve Saati *
                    </label>
                    <div className="flex flex-wrap items-center gap-1">
                        <span className="text-[11px] text-gray-400 mr-1">Hızlı Saat:</span>
                        <button
                            type="button"
                            onClick={applyNowTime}
                            className="px-2 py-0.5 text-[11px] font-medium rounded bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 hover:bg-blue-100"
                        >
                            Şimdi
                        </button>
                        {[
                            { label: '08:00', h: 8, m: 0 },
                            { label: '08:30', h: 8, m: 30 },
                            { label: '09:00', h: 9, m: 0 },
                            { label: '17:30', h: 17, m: 30 },
                            { label: '18:00', h: 18, m: 0 }
                        ].map((preset) => (
                            <button
                                key={preset.label}
                                type="button"
                                onClick={() => applyQuickTime(preset.h, preset.m)}
                                className="px-2 py-0.5 text-[11px] font-mono rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                </div>

                <CustomDateTimePicker
                    selectedDate={formData.recordTime}
                    onDateChange={(dateObj) => {
                        setFormData((prev) => ({ ...prev, recordTime: dateObj }));
                        setLocalError(null);
                    }}
                    label=""
                    placeholder="GG.AA.YYYY SS:DD"
                />
            </div>

            {/* Notlar ve Hızlı Açıklama Şablonları */}
            <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-1">
                    <label htmlFor="notes" className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        Notlar / Manuel Kayıt Gerekçesi
                    </label>
                    <span className="text-[11px] text-gray-400">Hızlı şablon eklemek için tıklayın</span>
                </div>

                <div className="flex flex-wrap gap-1.5">
                    {QUICK_NOTE_TEMPLATES.map((tpl) => (
                        <button
                            key={tpl}
                            type="button"
                            onClick={() => appendQuickNote(tpl)}
                            className="px-2.5 py-1 text-[11px] rounded-full bg-gray-100 dark:bg-gray-700/80 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-300 border border-gray-200 dark:border-gray-600 transition-colors"
                        >
                            + {tpl}
                        </button>
                    ))}
                </div>

                <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                    rows="2"
                    placeholder="Manuel geçiş kaydı ekleme/düzenleme nedenini yazabilirsiniz..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={submitting}
                />
            </div>

            {/* Alt Butonlar */}
            <div className="flex justify-end items-center gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={submitting}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg flex items-center gap-1.5 transition-colors"
                >
                    <XCircle className="w-4 h-4" />
                    İptal
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Kaydediliyor...
                        </>
                    ) : (
                        <>
                            <Save className="w-4 h-4" />
                            {isNew
                                ? entryMode === 'bulk'
                                    ? `Seçili Personelleri Kaydet (${selectedBulkEmployees.length})`
                                    : 'Manuel Kaydı Oluştur'
                                : 'Değişiklikleri Kaydet'}
                        </>
                    )}
                </button>
            </div>
        </form>
    );
}
